import React from "react";
import { motion } from "framer-motion";

const WhyTrustUs = () => {
  const features = [
    {
      title: "Quick Processing",
      description: "Streamlined digital process with minimal documentation requirements.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      image: "/images/quick.jpg",
    },
    {
      title: "Complete Transparency",
      description: "Clear terms and conditions with no hidden charges or fees.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      image: "/images/transparency.jpg",
    },
    {
      title: "Data Security",
      description: "Advanced encryption technology to protect your personal information.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      image: "/images/security.jpg",
    },
  ];

  const stats = [
    { value: "Trusted", label: "By Salaried Professionals", icon: "👥" },
    { value: "Transparent", label: "No Hidden Charges", icon: "⭐" },
    { value: "Secure", label: "Bank-Grade Data Protection", icon: "📊" },
  ];

  const keyFeatures = [
    {
      icon: "✓",
      text: "Instant approval within minutes"
    },
    {
      icon: "✓",
      text: "No hidden charges or fees"
    },
    {
      icon: "✓",
      text: "Bank-grade data encryption"
    },
    {
      icon: "✓",
      text: "Paperless digital process"
    }
  ];

  return (
    <section className="relative bg-[#0A1620] overflow-hidden py-20 lg:py-24">
      {/* Dark Theme Background */}
      <div className="absolute inset-0 bg-[#0A1620]"></div>

      {/* Premium Accent Gradients */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-[#1B4D5E]/20 rounded-full blur-[120px]"></div>
      <div className="absolute bottom-40 left-0 w-[400px] h-[400px] bg-[#1A3B4D]/20 rounded-full blur-[100px]"></div>
      
      {/* Subtle Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(to right, #2D5F7A 1px, transparent 1px), linear-gradient(to bottom, #2D5F7A 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        ></div>
      </div>

      {/* Top Wave */}
      <svg
        className="absolute top-0 left-0 w-full h-48 rotate-180"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <path
          fill="#2D5F7A"
          fillOpacity="0.08"
          d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
        ></path>
      </svg>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "80px" }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-0.5 bg-gradient-to-r from-[#7BC4E0] to-transparent mx-auto mb-6"
          />
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[#7BC4E0] font-mono text-sm font-semibold tracking-[0.3em] uppercase mb-4 block"
          >
            Why Trust Us
          </motion.span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Financial Solutions
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7BC4E0] to-[#4D9ABF] block mt-2">
              Designed for Professionals
            </span>
          </h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-lg md:text-xl text-[#CCE6F5] max-w-2xl mx-auto font-light"
          >
            We provide transparent and efficient financial services 
            <span className="text-[#7BC4E0] font-medium"> tailored to your needs.</span>
          </motion.p>
        </motion.div>

        {/* Features Grid - 3 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 + 0.3 }}
              whileHover={{
                y: -5,
                transition: { duration: 0.3 },
              }}
              className="group relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] border border-[#2D5F7A]/30"
              style={{
                boxShadow: "0 10px 30px -15px rgba(0, 0, 0, 0.5)",
              }}
            >
              {/* Animated Background Waves */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {/* Wave Layer 1 */}
                <motion.div
                  animate={{
                    x: [0, -30, 0, 30, 0],
                    y: [0, -5, 0, 5, 0],
                  }}
                  transition={{
                    duration: 12,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute -bottom-8 -left-10 w-48 h-48 rounded-full bg-[#7BC4E0]/5 blur-3xl"
                />
                {/* Wave Layer 2 */}
                <motion.div
                  animate={{
                    x: [0, 40, 0, -40, 0],
                    y: [0, 10, 0, -10, 0],
                  }}
                  transition={{
                    duration: 15,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-[#2D5F7A]/10 blur-3xl"
                />
                {/* Wave Layer 3 */}
                <motion.div
                  animate={{
                    x: [0, -50, 0, 50, 0],
                    y: [0, -15, 0, 15, 0],
                  }}
                  transition={{
                    duration: 18,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute bottom-20 right-20 w-64 h-64 rounded-full bg-[#1B4D5E]/10 blur-3xl"
                />
              </div>

              {/* Image Section */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1620] via-[#0A1620]/50 to-transparent"></div>
                
                {/* Icon Badge */}
                <div className="absolute bottom-4 left-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.5, type: "spring", bounce: 0.4 }}
                    className="w-12 h-12 bg-gradient-to-br from-[#2D5F7A] to-[#1E3B4A] rounded-xl flex items-center justify-center border border-[#7BC4E0]/30 shadow-lg"
                  >
                    <span className="text-[#7BC4E0] group-hover:text-white transition-colors duration-300">
                      {feature.icon}
                    </span>
                  </motion.div>
                </div>
              </div>

              {/* Content Section */}
              <div className="relative p-6">
                <motion.h3
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.6 }}
                  className="text-xl font-bold text-white mb-3"
                >
                  {feature.title}
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.7 }}
                  className="text-[#CCE6F5] text-sm leading-relaxed"
                >
                  {feature.description}
                </motion.p>

                {/* Bottom Accent Line */}
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.8, duration: 0.5 }}
                  className="absolute bottom-0 left-6 right-6 h-0.5 bg-gradient-to-r from-[#7BC4E0] to-transparent origin-left"
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats & Features Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 rounded-2xl overflow-hidden relative"
          style={{
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
          }}
        >
          {/* Background Image */}
          <img
            src="/images/back.avif"
            alt="Modern corporate building"
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Dark Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1620] via-[#1A2F3A] to-[#0A1620] opacity-95"></div>

          {/* Content */}
          <div className="relative px-8 py-12 md:py-16">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ scale: 0.9, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-2xl text-[#7BC4E0]">{stat.icon}</span>
                    <p className="text-3xl md:text-4xl font-bold text-white">{stat.value}</p>
                  </div>
                  <p className="text-[#CCE6F5] text-sm">{stat.label}</p>
                  <div className="w-12 h-0.5 bg-gradient-to-r from-[#7BC4E0] to-transparent mx-auto mt-3"></div>
                </motion.div>
              ))}
            </div>

            {/* Key Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
              {keyFeatures.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="flex items-center gap-3 px-4 py-3 bg-[#1E3B4A]/30 backdrop-blur-sm rounded-lg border border-[#2D5F7A]/20"
                >
                  <span className="text-[#7BC4E0] font-bold text-lg">{item.icon}</span>
                  <span className="text-[#CCE6F5] text-sm">{item.text}</span>
                </motion.div>
              ))}
            </div>

            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.9 }}
              className="flex items-center justify-center gap-3 mt-10 pt-6 border-t border-[#2D5F7A]/30"
            >
              <svg className="w-5 h-5 text-[#7BC4E0]" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-[#CCE6F5] text-sm">Trusted Financial Services Provider</span>
            </motion.div>
          </div>
        </motion.div>

       
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            x: ["0%", "2%", "0%"],
            y: [0, 10, 0],
          }}
          transition={{
            duration: 16,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="relative w-[110%] -left-[5%]"
        >
          <svg
            className="relative block w-full h-16 md:h-20"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
          >
            <motion.path
              animate={{
                d: [
                  "M0,64 C240,96 480,32 720,32 C960,32 1200,96 1440,64 L1440,120 L0,120 Z",
                  "M0,80 C240,48 480,112 720,112 C960,112 1200,48 1440,80 L1440,120 L0,120 Z",
                  "M0,64 C240,96 480,32 720,32 C960,32 1200,96 1440,64 L1440,120 L0,120 Z",
                ],
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              fill="#2D5F7A"
              fillOpacity="0.06"
            />
          </svg>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyTrustUs;